import { useState, useEffect, lazy, Suspense } from "react";
import { Lock, Phone, MessageSquareWarning, UserX } from "lucide-react";
import { type ReactNode } from "react";
import { ASCII_ART_RAW } from "../three/asciiArtData";

const AsciiScene = lazy(() => import("../three/AsciiScene"));

interface SecurityCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  borderColor: string;
}

function SecurityCard({ icon, title, description, borderColor }: SecurityCardProps) {
  return (
    <div
      className="card-accent-left bg-bg-secondary/50 backdrop-blur-sm p-5 sm:p-6 group hover:bg-surface-hover transition-all duration-300"
      style={{ borderLeftColor: borderColor }}
    >
      <div className="w-10 h-10 rounded-lg bg-bg-tertiary border border-surface-border flex items-center justify-center mb-3 text-accent-purple group-hover:glow-purple transition-all duration-300">
        {icon}
      </div>
      <h4 className="font-display text-lg font-semibold text-white mb-1">
        {title}
      </h4>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

const securityFeatures: SecurityCardProps[] = [
  {
    icon: <Lock size={20} />,
    title: "Crypto Escrow Protection",
    description:
      "All payments are locked in escrow before any work begins. Funds are only released upon mutual agreement or dispute resolution.",
    borderColor: "#7C3AED",
  },
  {
    icon: <Phone size={20} />,
    title: "Phone-Verified Users",
    description:
      "Every user must verify via phone to prevent scams and sock puppet accounts. Real people, anonymous identities.",
    borderColor: "#8B5CF6",
  },
  {
    icon: <MessageSquareWarning size={20} />,
    title: "Private Anonymous Chat",
    description:
      "Communicate securely before hiring. Encrypted messaging with full anonymity — no personal info shared until you decide.",
    borderColor: "#A78BFA",
  },
  {
    icon: <UserX size={20} />,
    title: "Anonymous Profiles Until Payment",
    description:
      "No personal information is shared until a contract is signed and escrow is funded. Your identity stays private.",
    borderColor: "#C4B5FD",
  },
];

export default function Security() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <section id="security" className="section-padding relative">
      <div className="section-container relative" style={{ zIndex: 1 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: ASCII logo */}
          <div className="relative hidden lg:block h-[520px]">
            {/* 3D ASCII Scene behind */}
            {!isMobile && (
              <div className="absolute inset-0 pointer-events-auto">
                <Suspense fallback={null}>
                  <AsciiScene className="w-full h-full" />
                </Suspense>
              </div>
            )}
          </div>

          {/* Right: Text Content */}
          <div className="relative">
            {/* Section header — left-aligned with purple accent line */}
            <div className="accent-line-purple mb-10">
              <span className="text-accent-purple uppercase tracking-widest text-xs font-mono mb-3 block">
                Security First
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Built for{" "}
                <span className="text-gradient">trust and privacy</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed">
                Every layer of HirenFire is designed to protect both agencies and
                workers. From crypto escrow to anonymous profiles, your security
                is our foundation.
              </p>
            </div>

            {/* Feature cards — stacked vertically */}
            <div className="space-y-4">
              {securityFeatures.map((feature) => (
                <SecurityCard key={feature.title} {...feature} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile fallback — ASCII art */}
      {isMobile && (
        <div className="flex items-center justify-center py-8">
          <pre className="text-[4px] sm:text-[6px] text-accent-purple/70 font-mono leading-none select-none whitespace-pre">
            {ASCII_ART_RAW}
          </pre>
        </div>
      )}
    </section>
  );
}
